import { useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import {
  clearRememberedAuthRedirect,
  isAllowedExternalRedirect,
  rememberAuthRedirect,
  resolveAuthRedirect,
  redirectToExternalAuth,
} from '@/lib/auth-redirect';

/**
 * After login/signup, send users back to the marketing site when ?redirect= is set.
 * Runs globally so React Router cannot race ahead to /accounts.
 *
 * Skip / clear when intentionally opening the app (e.g. POA "Open dashboard"
 * uses ?noredirect=1 so a stale marketing return URL cannot bounce them back).
 */
export default function AuthRedirectListener() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const redirectParam = searchParams.get('redirect');
  const noRedirect =
    searchParams.get('noredirect') === '1' ||
    searchParams.get('from') === 'poa_paid' ||
    searchParams.get('from') === 'checkout';

  useEffect(() => {
    if (noRedirect) {
      clearRememberedAuthRedirect();
      const next = new URLSearchParams(searchParams);
      next.delete('noredirect');
      next.delete('from');
      setSearchParams(next, { replace: true });
      return;
    }
    if (redirectParam) {
      rememberAuthRedirect(redirectParam);
    }
  }, [redirectParam, noRedirect, searchParams, setSearchParams]);

  useEffect(() => {
    if (loading || !user || noRedirect) return;

    const target = resolveAuthRedirect(redirectParam);
    if (!isAllowedExternalRedirect(target)) return;

    redirectToExternalAuth(target);
  }, [loading, user, redirectParam, noRedirect, location.pathname, location.search]);

  return null;
}
